import { useEffect, useState } from "react";
import useSWR, { mutate as swrMutate } from "swr";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import client, { fetcher } from "@/lib/api";
import {
  ShieldCheck,
  Copy,
  Plus,
  Trash2,
  Loader2,
  Eye,
  Download,
  Info,
  X,
  Save,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { SCOPE_NAMES } from "@/config/scope";

const OPERATORS = [
  { key: "$eq", label: "es igual a", scalar: true },
  { key: "$ne", label: "es distinto de", scalar: true },
  { key: "$in", label: "está en la lista", scalar: false },
  { key: "$nin", label: "no está en la lista", scalar: false },
  { key: "$exists", label: "existe (true/false)", boolean: true },
  { key: "$regex", label: "coincide con patrón", scalar: true },
];

// Convertir un row_filter Mongo a filas de builder
function filterToRows(rf) {
  const out = [];
  Object.entries(rf || {}).forEach(([field, val]) => {
    if (field.startsWith("$")) return;
    if (val && typeof val === "object" && !Array.isArray(val)) {
      const [op, v] = Object.entries(val)[0] || ["$eq", ""];
      out.push({ field, op, value: Array.isArray(v) ? v.join(",") : String(v) });
    } else {
      const isArr = Array.isArray(val);
      out.push({
        field,
        op: isArr ? "$in" : "$eq",
        value: isArr ? val.join(",") : String(val),
      });
    }
  });
  return out;
}

function rowsToFilter(rows) {
  const out = {};
  for (const r of rows) {
    if (!r.field || !r.op) continue;
    if (r.op === "$in" || r.op === "$nin") {
      const arr = r.value.split(",").map((s) => s.trim()).filter(Boolean);
      out[r.field] = { [r.op]: arr };
    } else if (r.op === "$exists") {
      out[r.field] = { $exists: String(r.value).toLowerCase() === "true" };
    } else if (r.op === "$eq") {
      out[r.field] =
        r.value === "true" || r.value === "false"
          ? r.value === "true"
          : r.value;
    } else {
      out[r.field] = { [r.op]: r.value };
    }
  }
  return out;
}

export default function ApiExposicion() {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("es") ? "es" : "gl";
  const { data: fieldsData } = useSWR("/admin/fleet/fields", fetcher);
  const { data, isLoading } = useSWR("/admin/fleet/exposure-profiles", fetcher);
  const [editing, setEditing] = useState(null); // slug being edited
  const [creating, setCreating] = useState(false);

  const profiles = data?.profiles || [];
  const fields = fieldsData?.exposable || [];

  return (
    <div data-testid="api-exposicion-root">
      {/* Hero + RGPD banner */}
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.24em] text-salnes-atlantic mb-2">
          {lang === "es" ? "API pública · flota" : "API pública · flota"}
        </div>
        <h2 className="font-serif text-3xl text-salnes-ink leading-tight">
          {lang === "es"
            ? "Exposición de la API de flota"
            : "Exposición da API de flota"}
        </h2>
        <p className="mt-2 text-salnes-stone text-sm max-w-3xl leading-relaxed">
          {lang === "es"
            ? "Decide qué campos y qué filas de la flota se comparten con cada consumidor externo (Segittur, concellos, público). Los cambios se aplican al instante y respetan la política RGPD y el scope territorial contractual."
            : "Decide que campos e que filas da flota se comparten con cada consumidor externo (Segittur, concellos, público). Os cambios aplícanse ao instante e respectan a política RGPD e o scope territorial contractual."}
        </p>
      </div>

      <div
        className="rounded-2xl border border-amber-200 bg-amber-50 p-4 mb-8 flex items-start gap-3"
        data-testid="api-exp-rgpd-banner"
      >
        <Info size={18} className="text-amber-700 mt-0.5 shrink-0" />
        <div className="text-sm text-amber-900 leading-snug">
          <b>Recuerda:</b>{" "}
          {lang === "es"
            ? "no se pueden exponer datos personales del conductor ni identificadores únicos del vehículo (VIN, chasis). Estos campos están bloqueados por diseño y no aparecen en la lista."
            : "non se poden expor datos persoais do condutor nin identificadores únicos do vehículo (VIN, chasis). Estes campos están bloqueados por deseño e non aparecen na lista."}
        </div>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div className="text-salnes-stone text-sm" data-testid="api-exp-count">
          {isLoading
            ? "…"
            : lang === "es"
              ? `${profiles.length} perfiles configurados`
              : `${profiles.length} perfís configurados`}
        </div>
        <Button
          className="rounded-full"
          onClick={() => setCreating(true)}
          data-testid="api-exp-create-btn"
        >
          <Plus size={16} className="mr-2" />
          {lang === "es" ? "Crear perfil" : "Crear perfil"}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {profiles.map((p) => (
          <ProfileCard
            key={p.slug}
            profile={p}
            onEdit={() => setEditing(p.slug)}
          />
        ))}
      </div>

      {editing && (
        <ProfileEditor
          slug={editing}
          fields={fields}
          onClose={() => setEditing(null)}
        />
      )}
      {creating && (
        <ProfileCreator
          fields={fields}
          onClose={() => setCreating(false)}
        />
      )}
    </div>
  );
}

function ProfileCard({ profile, onEdit }) {
  const enabled = !!profile.enabled;
  const publicUrl = `${window.location.origin}/api/public/fleet?profile=${profile.slug}`;
  return (
    <div
      className="rounded-3xl border border-salnes-stone/10 bg-white p-6 hover:shadow-xl transition-shadow"
      data-testid={`api-exp-card-${profile.slug}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="font-serif text-xl text-salnes-ink leading-tight">
            {profile.name}
          </div>
          <div className="text-[11px] text-salnes-stone mt-0.5">
            /{profile.slug}
            {profile.is_system && (
              <span className="ml-2 text-amber-600">· sistema</span>
            )}
          </div>
        </div>
        <Badge
          className={
            enabled
              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
              : "bg-salnes-stone/10 text-salnes-stone border border-salnes-stone/20"
          }
        >
          {enabled ? "Activo" : "Inactivo"}
        </Badge>
      </div>
      <p className="text-sm text-salnes-stone mb-4 line-clamp-2 min-h-[42px]">
        {profile.description || "Sin descripción."}
      </p>
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {profile.allowed_fields.map((f) => (
          <span
            key={f}
            className="text-[10px] bg-salnes-ivory border border-salnes-stone/10 rounded-full px-2 py-0.5 text-salnes-ink"
          >
            {f}
          </span>
        ))}
      </div>
      <div className="mb-4">
        <div className="text-[10px] uppercase tracking-[0.22em] text-salnes-stone mb-1">
          URL pública
        </div>
        <div className="flex items-center gap-2">
          <code className="flex-1 text-[11px] bg-salnes-ivory px-2 py-1.5 rounded truncate text-salnes-atlantic">
            {publicUrl}
          </code>
          <Button
            size="icon"
            variant="ghost"
            className="h-8 w-8"
            onClick={() => {
              navigator.clipboard.writeText(publicUrl);
              toast.success("URL copiada al portapapeles");
            }}
            data-testid={`api-exp-copy-${profile.slug}`}
          >
            <Copy size={14} />
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          className="rounded-full flex-1"
          onClick={onEdit}
          data-testid={`api-exp-edit-${profile.slug}`}
        >
          Editar
        </Button>
      </div>
    </div>
  );
}

function ProfileEditor({ slug, fields, onClose }) {
  const { data: profile, mutate: mutateProfile } = useSWR(
    `/admin/fleet/exposure-profiles/${slug}`,
    fetcher
  );
  const [form, setForm] = useState(null);
  const [rows, setRows] = useState([]);
  const [rawMode, setRawMode] = useState(false);
  const [rawJson, setRawJson] = useState("{}");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (profile) {
      setForm({
        name: profile.name,
        description: profile.description || "",
        allowed_fields: profile.allowed_fields || [],
        enabled: !!profile.enabled,
      });
      setRows(filterToRows(profile.row_filter));
      setRawJson(JSON.stringify(profile.row_filter || {}, null, 2));
    }
  }, [profile]);

  if (!profile || !form)
    return (
      <Dialog open={true} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl">
          <DialogHeader className="sr-only">
            <DialogTitle>Cargando perfil…</DialogTitle>
            <DialogDescription>
              Recuperando la configuración del perfil de exposición.
            </DialogDescription>
          </DialogHeader>
          <div className="p-8 text-center">
            <Loader2 className="animate-spin mx-auto" />
          </div>
        </DialogContent>
      </Dialog>
    );

  const isSystem = profile.is_system;

  const toggleField = (k) => {
    setForm((f) => ({
      ...f,
      allowed_fields: f.allowed_fields.includes(k)
        ? f.allowed_fields.filter((x) => x !== k)
        : [...f.allowed_fields, k],
    }));
  };

  const buildRowFilter = () => {
    if (rawMode) {
      try {
        return JSON.parse(rawJson || "{}");
      } catch {
        toast.error("JSON inválido en filtro avanzado");
        throw new Error("bad json");
      }
    }
    return rowsToFilter(rows);
  };

  const save = async () => {
    setSaving(true);
    try {
      const row_filter = buildRowFilter();
      const body = { description: form.description };
      if (!isSystem) {
        body.name = form.name;
        body.allowed_fields = form.allowed_fields;
        body.row_filter = row_filter;
        body.enabled = form.enabled;
      }
      await client.patch(`/admin/fleet/exposure-profiles/${slug}`, body);
      toast.success("Perfil guardado");
      await mutateProfile();
      await swrMutate("/admin/fleet/exposure-profiles");
      onClose();
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Error al guardar";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    try {
      const row_filter = buildRowFilter();
      // If unsaved changes are present, save a shadow patch first? No — just
      // preview from the persisted version. The user must save to test edits.
      const r = await client.get(
        `/admin/fleet/exposure-preview?profile=${slug}&limit=5`
      );
      setPreview(r.data);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "No se pudo generar la previsualización"
      );
    }
  };

  const removeProfile = async () => {
    if (!window.confirm(`¿Eliminar el perfil "${slug}"? Esta acción no se puede deshacer.`))
      return;
    try {
      await client.delete(`/admin/fleet/exposure-profiles/${slug}`);
      toast.success("Perfil eliminado");
      await swrMutate("/admin/fleet/exposure-profiles");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const downloadCsv = () => {
    const token = sessionStorage.getItem("salnes_token");
    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/admin/fleet/exposure-profiles/${slug}/csv`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `salnes-fleet-${slug}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      });
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid={`api-exp-editor-${slug}`}
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <ShieldCheck className="text-salnes-atlantic" size={20} />
            {form.name}{" "}
            <span className="text-salnes-stone text-sm font-sans">
              /{slug}
            </span>
            {isSystem && (
              <Badge className="bg-amber-50 text-amber-700 border border-amber-200 ml-1">
                sistema
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            {isSystem
              ? "Perfil del sistema: sólo la descripción es editable."
              : "Configura qué campos y qué filas verán los consumidores externos."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <Section title="Nombre y descripción">
            <div className="space-y-3">
              <Field label="Nombre">
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  disabled={isSystem}
                  data-testid="api-exp-field-name"
                />
              </Field>
              <Field label="Descripción">
                <Input
                  value={form.description}
                  onChange={(e) =>
                    setForm({ ...form, description: e.target.value })
                  }
                  data-testid="api-exp-field-description"
                />
              </Field>
              {!isSystem && (
                <div className="flex items-center gap-3 py-2">
                  <Switch
                    checked={form.enabled}
                    onCheckedChange={(v) => setForm({ ...form, enabled: v })}
                    data-testid="api-exp-field-enabled"
                  />
                  <span className="text-sm text-salnes-ink">
                    Activo — {form.enabled ? "expuesto públicamente" : "oculto"}
                  </span>
                </div>
              )}
            </div>
          </Section>

          <Section title="¿Qué campos comparte?">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {fields.map((f) => {
                const checked = form.allowed_fields.includes(f.key);
                return (
                  <label
                    key={f.key}
                    className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                      checked
                        ? "border-salnes-atlantic bg-salnes-atlantic/5"
                        : "border-salnes-stone/15 hover:border-salnes-stone/30"
                    } ${isSystem ? "opacity-60 cursor-not-allowed" : ""}`}
                    data-testid={`api-exp-field-checkbox-${f.key}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={isSystem}
                      onChange={() => !isSystem && toggleField(f.key)}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-salnes-ink">
                        {f.es || f.key}
                        <span className="ml-1 font-mono text-[10px] text-salnes-stone">
                          {f.key}
                        </span>
                      </div>
                      <div className="text-[11px] text-salnes-stone mt-0.5 leading-snug">
                        {f.sub_es}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </Section>

          <Section
            title="¿Qué filas comparte?"
            hint="Filtra los vehículos que se envían. Vacío = todos los del scope territorial."
          >
            <div className="flex items-center justify-end gap-2 mb-3">
              <Switch
                checked={rawMode}
                onCheckedChange={setRawMode}
                disabled={isSystem}
                data-testid="api-exp-filter-raw-toggle"
              />
              <span className="text-xs text-salnes-stone">Editor avanzado (JSON)</span>
            </div>
            {rawMode ? (
              <textarea
                value={rawJson}
                onChange={(e) => setRawJson(e.target.value)}
                disabled={isSystem}
                className="w-full h-40 rounded-lg border border-salnes-stone/20 p-3 font-mono text-xs"
                data-testid="api-exp-filter-raw"
              />
            ) : (
              <FilterBuilder
                rows={rows}
                setRows={setRows}
                fields={form.allowed_fields}
                disabled={isSystem}
              />
            )}
          </Section>

          {preview && (
            <Section title={`Previsualización · ${preview.total_matching} vehículos coincidentes`}>
              <div className="rounded-xl border border-salnes-stone/15 bg-salnes-ivory overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-white border-b hairline">
                    <tr>
                      {(preview.allowed_fields || []).map((f) => (
                        <th
                          key={f}
                          className="text-left px-3 py-2 font-medium text-salnes-stone uppercase tracking-wider text-[10px]"
                        >
                          {f}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y hairline">
                    {(preview.sample || []).map((row, i) => (
                      <tr key={i} data-testid={`api-exp-preview-row-${i}`}>
                        {(preview.allowed_fields || []).map((f) => (
                          <td key={f} className="px-3 py-2 text-salnes-ink">
                            {row[f] === true
                              ? "true"
                              : row[f] === false
                                ? "false"
                                : row[f] ?? "—"}
                          </td>
                        ))}
                      </tr>
                    ))}
                    {(!preview.sample || preview.sample.length === 0) && (
                      <tr>
                        <td
                          colSpan={preview.allowed_fields?.length || 1}
                          className="p-6 text-center text-salnes-stone italic"
                        >
                          Sin vehículos coincidentes.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Section>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 border-t hairline">
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="rounded-full"
              onClick={runPreview}
              data-testid="api-exp-preview-btn"
            >
              <Eye size={14} className="mr-2" />
              Previsualizar
            </Button>
            <Button
              variant="outline"
              className="rounded-full"
              onClick={downloadCsv}
              data-testid="api-exp-csv-btn"
            >
              <Download size={14} className="mr-2" />
              CSV
            </Button>
          </div>
          <div className="flex gap-2">
            {!isSystem && (
              <Button
                variant="outline"
                className="rounded-full text-rose-700 border-rose-200 hover:bg-rose-50"
                onClick={removeProfile}
                data-testid="api-exp-delete-btn"
              >
                <Trash2 size={14} className="mr-2" />
                Eliminar
              </Button>
            )}
            <Button variant="outline" className="rounded-full" onClick={onClose}>
              <X size={14} className="mr-2" />
              Cancelar
            </Button>
            <Button
              className="rounded-full"
              onClick={save}
              disabled={saving}
              data-testid="api-exp-save-btn"
            >
              {saving ? (
                <Loader2 className="animate-spin mr-2" size={14} />
              ) : (
                <Save size={14} className="mr-2" />
              )}
              Guardar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FilterBuilder({ rows, setRows, fields, disabled }) {
  const addRow = () =>
    setRows([...rows, { field: fields[0] || "municipality", op: "$eq", value: "" }]);
  const updateRow = (i, patch) =>
    setRows(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2" data-testid="api-exp-filter-builder">
      {rows.map((r, i) => {
        const op = OPERATORS.find((o) => o.key === r.op);
        return (
          <div key={i} className="flex flex-wrap items-center gap-2">
            <Select
              value={r.field}
              onValueChange={(v) => updateRow(i, { field: v })}
              disabled={disabled}
            >
              <SelectTrigger className="w-40 h-9">
                <SelectValue placeholder="Campo" />
              </SelectTrigger>
              <SelectContent>
                {["municipality", "plate", "fuel_type", "has_gps_active", "brand", "model"].map((f) => (
                  <SelectItem key={f} value={f}>
                    {f}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={r.op}
              onValueChange={(v) => updateRow(i, { op: v })}
              disabled={disabled}
            >
              <SelectTrigger className="w-44 h-9">
                <SelectValue placeholder="Operador" />
              </SelectTrigger>
              <SelectContent>
                {OPERATORS.map((o) => (
                  <SelectItem key={o.key} value={o.key}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {op?.boolean ? (
              <Select
                value={r.value || "true"}
                onValueChange={(v) => updateRow(i, { value: v })}
                disabled={disabled}
              >
                <SelectTrigger className="w-32 h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">sí</SelectItem>
                  <SelectItem value="false">no</SelectItem>
                </SelectContent>
              </Select>
            ) : r.field === "municipality" && (r.op === "$in" || r.op === "$nin") ? (
              <Input
                value={r.value}
                placeholder={SCOPE_NAMES.join(", ")}
                onChange={(e) => updateRow(i, { value: e.target.value })}
                disabled={disabled}
                className="flex-1 min-w-[240px] h-9"
              />
            ) : (
              <Input
                value={r.value}
                placeholder={
                  op?.scalar ? "Valor" : "Separados por coma"
                }
                onChange={(e) => updateRow(i, { value: e.target.value })}
                disabled={disabled}
                className="flex-1 min-w-[200px] h-9"
              />
            )}
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8 text-rose-600"
              disabled={disabled}
              onClick={() => removeRow(i)}
            >
              <Trash2 size={14} />
            </Button>
          </div>
        );
      })}
      <Button
        size="sm"
        variant="outline"
        className="rounded-full"
        onClick={addRow}
        disabled={disabled}
        data-testid="api-exp-filter-add-row"
      >
        <Plus size={14} className="mr-2" />
        Añadir condición
      </Button>
    </div>
  );
}

function ProfileCreator({ fields, onClose }) {
  const [form, setForm] = useState({
    slug: "",
    name: "",
    description: "",
    allowed_fields: ["municipality", "fuel_type", "has_gps_active"],
    enabled: false,
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.slug.trim() || !form.name.trim()) {
      toast.error("Slug y nombre son obligatorios");
      return;
    }
    setSaving(true);
    try {
      await client.post("/admin/fleet/exposure-profiles", {
        ...form,
        row_filter: {},
      });
      toast.success("Perfil creado");
      await swrMutate("/admin/fleet/exposure-profiles");
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al crear");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="api-exp-creator">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Crear perfil nuevo</DialogTitle>
          <DialogDescription>
            Se creará como inactivo. Actívalo cuando confirmes que los campos son correctos.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <Field label="Slug (usado en la URL pública)">
            <Input
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder="ej: opendata"
              data-testid="api-exp-new-slug"
            />
          </Field>
          <Field label="Nombre">
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="api-exp-new-name"
            />
          </Field>
          <Field label="Descripción">
            <Input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              data-testid="api-exp-new-description"
            />
          </Field>
          <div>
            <Label className="text-xs uppercase tracking-wider text-salnes-stone">
              Campos iniciales
            </Label>
            <div className="text-[11px] text-salnes-stone mt-1">
              Municipality + fuel_type + has_gps_active seleccionados por defecto.
              Se pueden ajustar tras crear.
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 pt-4 border-t hairline">
          <Button variant="outline" onClick={onClose} className="rounded-full">
            Cancelar
          </Button>
          <Button
            className="rounded-full"
            onClick={submit}
            disabled={saving}
            data-testid="api-exp-new-submit"
          >
            {saving ? <Loader2 className="animate-spin mr-2" size={14} /> : null}
            Crear
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, hint, children }) {
  return (
    <section>
      <div className="text-[10px] uppercase tracking-[0.22em] text-salnes-stone mb-2">
        {title}
      </div>
      {hint && (
        <div className="text-[11px] text-salnes-stone italic mb-3">{hint}</div>
      )}
      {children}
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.18em] text-salnes-stone">
        {label}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
